import NiceModal from '@ebay/nice-modal-react';
import { Alert, Box, Group, Paper } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';

import { mapMaterial, useGetContentMaterials, useContentManagerId } from '../api';
import { useContentPages } from '../hooks/useContentPages';
import { contentTypes } from '../types';

import { CategoriesPanel } from './Categories/CategoriesPanel';
import { CategoryFilter } from './CategoryFilter';
import { ContentHeader } from './Header';
import { PickTypeModal } from './PickTypeModal';
import { StatusFilter } from './StatusFilter';
import { ContentTable } from './Table';
import { ContentTabValue, ContentTabs } from './Tabs';

import { ListLoader } from '@/components';
import { Platform } from '@/features/pages';
import { Departments } from '@/types';
import storage from '@/utils/storage';

type Props = {
  department: Departments;
};

const TAB_TYPES: contentTypes.ContentType[] = ['text', 'photo', 'video', 'audio', 'document'];

export const ContentList = ({ department }: Props) => {
  const { pages, pagesByPlatform, isLoading: isPagesLoading } = useContentPages(department);
  const {
    managerId,
    isLoading: isAuthLoading,
    isReady,
    needsManager,
  } = useContentManagerId(department);

  const initialPlatform = (storage.getContentPlatform() as Platform) || 'instagram';
  const [platform, setPlatform] = useState<Platform>(initialPlatform);
  const [selectedPageId, setSelectedPageId] = useState<string | null>(
    () => storage.getContentPageIds()[`${department}:${initialPlatform}`] ?? null
  );
  const [activeTab, setActiveTab] = useState<ContentTabValue>('text');
  // Spec: filters reset on type-tab / page change — single values, not per-tab maps
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<contentTypes.Status | null>(null);

  const platformPages = pagesByPlatform[platform];

  const handlePlatformChange = (next: Platform) => {
    storage.setContentPlatform(next);
    setPlatform(next);
  };

  const handleSelectPage = (id: string) => {
    storage.setContentPageId(`${department}:${platform}`, id);
    setSelectedPageId(id);
  };

  useEffect(() => {
    if (selectedPageId && platformPages.some((page) => page.pageId === selectedPageId)) {
      return;
    }
    const storedPageId = storage.getContentPageIds()[`${department}:${platform}`];
    const fallbackPageId =
      storedPageId && platformPages.some((page) => page.pageId === storedPageId)
        ? storedPageId
        : platformPages[0]?.pageId;
    setSelectedPageId(fallbackPageId ?? null);
  }, [platformPages, selectedPageId, department, platform]);

  useEffect(() => {
    setCategoryFilter(null);
    setStatusFilter(null);
  }, [selectedPageId]);

  const materialsEnabled = isReady && Boolean(selectedPageId);

  const { data: materialsData, isLoading: isMaterialsLoading } = useGetContentMaterials({
    pageId: selectedPageId,
    managerId,
    config: { enabled: materialsEnabled },
  });

  const scopedItems = useMemo(
    () =>
      (materialsData?.materials ?? []).map((material) =>
        mapMaterial(material, department, platform)
      ),
    [materialsData?.materials, department, platform]
  );

  const counts = useMemo(() => {
    const init = TAB_TYPES.reduce(
      (acc, type) => {
        acc[type] = 0;
        return acc;
      },
      {} as Record<contentTypes.ContentType, number>
    );
    for (const item of scopedItems) {
      init[item.type] += 1;
    }
    return init;
  }, [scopedItems]);

  const visibleItems = useMemo(() => {
    if (activeTab === 'categories') return [];
    return scopedItems
      .filter((item) => item.type === activeTab)
      .filter((item) => !categoryFilter || item.category === categoryFilter)
      .filter((item) => !statusFilter || item.status === statusFilter);
  }, [scopedItems, activeTab, categoryFilter, statusFilter]);

  const handleTabChange = (tab: ContentTabValue) => {
    setActiveTab(tab);
    setCategoryFilter(null);
    setStatusFilter(null);
  };

  const handleAdd = () => {
    if (!selectedPageId) return;
    NiceModal.show(PickTypeModal, {
      department,
      platform,
      pageId: selectedPageId,
    });
  };

  if (isPagesLoading || isAuthLoading) {
    return <ListLoader />;
  }

  return (
    <Box>
      <ContentHeader
        department={department}
        platform={platform}
        onPlatformChange={handlePlatformChange}
        pages={platformPages}
        selectedPageId={selectedPageId}
        onSelectPage={handleSelectPage}
        onAdd={handleAdd}
      />

      {pages.length === 0 && (
        <Alert color="blue" mt="md" title="Нет страниц">
          Подключите Instagram или Telegram страницы в разделе Pages, чтобы управлять контентом.
        </Alert>
      )}

      {needsManager && (
        <Alert color="yellow" mt="md" title="Нужен менеджер">
          Создайте менеджера в разделе Managers для региона {department.toUpperCase()}.
        </Alert>
      )}

      <ContentTabs active={activeTab} counts={counts} onChange={handleTabChange} />

      {activeTab === 'categories' ? (
        <CategoriesPanel pageId={selectedPageId} materials={scopedItems} />
      ) : (
        <Paper mt="md" radius="md" p="xs" withBorder={false} shadow="xs">
          <Group justify="flex-end" px="xs" pt="xs" pb="sm" gap="md" wrap="wrap">
            <StatusFilter value={statusFilter} onChange={setStatusFilter} />
            <CategoryFilter
              pageId={selectedPageId}
              value={categoryFilter}
              onChange={setCategoryFilter}
            />
          </Group>
          <ContentTable
            items={visibleItems}
            pageId={selectedPageId ?? undefined}
            managerId={managerId}
            isLoading={materialsEnabled && isMaterialsLoading}
            emptyMessage={
              statusFilter || categoryFilter
                ? 'Нет материалов по выбранным фильтрам'
                : 'Нет контента в этой вкладке'
            }
          />
        </Paper>
      )}
    </Box>
  );
};
